import { call, put, race, take, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';

import { AnyRecord } from '../../utils';

import { prompt, showPrompt, addModalProps, reset } from './slice';
import { PromptPropsWithParams } from './PromptProvider.types';

export function* promptWatcher(
    action: PayloadAction<
        void,
        string,
        { deferred: { resolve: (val: boolean) => void }; confirmAction: () => void }
    >,
) {
    const {
        meta: { deferred, confirmAction },
    } = action;

    const { success } = yield race({
        success: take(prompt.SUCCESS),
        failure: take(prompt.FAILURE),
        navigate: take('@@router/LOCATION_CHANGE'),
    });

    if (success) {
        if (typeof confirmAction === 'function') {
            yield put(prompt.request());
            yield put(addModalProps({ isLoading: true }));

            try {
                yield call(confirmAction);
            } catch {
                // noop, should be handled by error listener /error saga
            }
        }

        if (deferred) {
            yield call(deferred.resolve, true);
        }
    } else if (deferred) {
        yield call(deferred.resolve, false);
    }

    yield put(reset());
    yield put(prompt.fulfill({ success: !!success }));
}

export function* withPrompt<Params extends AnyRecord>(
    promptConfig: PromptPropsWithParams<Params>,
    confirmAction?: () => void,
) {
    yield put(
        showPrompt({ config: confirmAction ? { ...promptConfig, confirmAction } : promptConfig }),
    );

    const {
        payload: { success },
    } = yield take(prompt.FULFILL);
    return success;
}

export default function* () {
    yield takeLatest(showPrompt.type, promptWatcher);
}
